import React from 'react';
import DOMPurify from 'dompurify';

const LegalDocumentPreviewModal = ({ 
  isOpen, 
  onClose, 
  title, 
  content,
  lastUpdated 
}) => {
  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop - click disabled */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">Preview: {title}</h2>
                {lastUpdated && (
                  <p className="text-sm text-gray-500">
                    Last updated: {formatDate(lastUpdated)}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Browser-like Preview Frame */}
          <div className="flex-1 overflow-hidden bg-gray-100 p-4">
            <div className="bg-white rounded-lg shadow-inner border border-gray-200 h-full overflow-auto">
              {/* Simulated Browser Bar */}
              <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex items-center gap-2 sticky top-0">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 ml-4">
                  <div className="bg-white border border-gray-300 rounded px-3 py-1 text-sm text-gray-500 truncate">
                    siteorbit.app/{title.toLowerCase().replace(/\s+/g, '-')}
                  </div>
                </div>
              </div>

              {/* Document Content */}
              <div className="p-8 md:p-12">
                <div 
                  className="prose prose-lg max-w-none
                    prose-headings:text-gray-800 
                    prose-h1:text-3xl prose-h1:font-bold prose-h1:mb-6 prose-h1:pb-4 prose-h1:border-b prose-h1:border-gray-200
                    prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-gray-700
                    prose-h3:text-lg prose-h3:font-medium prose-h3:mt-6 prose-h3:mb-3 prose-h3:text-gray-700
                    prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-4
                    prose-ul:my-4 prose-ul:pl-6
                    prose-li:text-gray-600 prose-li:mb-2
                    prose-a:text-yellow-600 prose-a:hover:text-yellow-700 prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-gray-800
                    prose-table:border-collapse prose-table:w-full
                    prose-th:border prose-th:border-gray-200 prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left
                    prose-td:border prose-td:border-gray-200 prose-td:p-3"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content || '<p class="text-gray-400 italic">No content available</p>') }}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg transition-colors"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentPreviewModal;
